import React, { useState } from 'react';
import { CalendarIcon, UserIcon, SunIcon } from '@heroicons/react/24/outline';
import { PlanningTab } from './tabs/PlanningTab';
import { InformationPersonnelleTab } from './tabs/InformationPersonnelleTab';
import { CongesTab } from './tabs/CongesTab';
import { useUnvalidatedWeeksCount } from './hooks/useUnvalidatedWeeksCount';
import './MonEspaceTab.css';

type TabType = 'planning' | 'conges' | 'info';

const TABS: { value: TabType; label: string; subtitle: string; Icon: typeof CalendarIcon }[] = [
  { value: 'planning', label: 'Planning',            subtitle: 'Gérez votre planning de disponibilité', Icon: CalendarIcon },
  { value: 'conges',   label: 'Congés',              subtitle: 'Demandez et suivez vos congés',         Icon: SunIcon },
  { value: 'info',     label: 'Informations perso',  subtitle: 'Gérez vos informations perso',          Icon: UserIcon },
];

export const MonEspaceTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('planning');
  // When the user clicks "Valider" on a week in the Congés tab, we jump to the
  // Planning tab opened on that week so they can review/correct + certify it.
  const [planningInitialWeek, setPlanningInitialWeek] = useState<string | null>(null);
  const { count: unvalidatedWeeksCount } = useUnvalidatedWeeksCount();

  const handleValidateWeek = (weekStart: string) => {
    setPlanningInitialWeek(weekStart);
    setActiveTab('planning');
  };

  const activeMeta = TABS.find(t => t.value === activeTab)!;

  return (
    <div className="mon-espace-page-container">
      {/* Page Header */}
      <div className="mon-espace-header">
        <div className="mon-espace-header-content">
          <div className="mon-espace-title-row">
            <div>
              <h1 className="mon-espace-title">Mon Espace</h1>
              <p className="mon-espace-subtitle">{activeMeta.subtitle}</p>
            </div>

            {/* 3-way toggle — ToggleSwitch atom only handles 2 options, so this
                uses a local segmented control. Congés gets a badge when the
                user has unvalidated past weeks. */}
            <div className="mon-espace-toggle-wrapper">
              <div className="mon-espace-segmented" role="tablist" aria-label="Navigation Mon Espace">
                {TABS.map(({ value, label, Icon }) => {
                  const isActive = activeTab === value;
                  const showBadge = value === 'conges' && unvalidatedWeeksCount > 0;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`mon-espace-segment ${isActive ? 'is-active' : ''}`}
                      onClick={() => {
                        // Manual tab switch → drop any pending validation target
                        // so Planning opens on the current week as usual.
                        setPlanningInitialWeek(null);
                        setActiveTab(value);
                      }}
                    >
                      <Icon className="mon-espace-segment-icon" />
                      <span>{label}</span>
                      {showBadge && (
                        <span className="mon-espace-segment-badge" title={`${unvalidatedWeeksCount} semaine${unvalidatedWeeksCount > 1 ? 's' : ''} à valider`}>
                          {unvalidatedWeeksCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Action buttons will be rendered by the active tab */}
          <div id="mon-espace-actions-portal"></div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mon-espace-content">
        <div className="mon-espace-content-inner">
          {activeTab === 'planning' && <PlanningTab initialWeek={planningInitialWeek} />}
          {activeTab === 'conges'   && <CongesTab onValidateWeek={handleValidateWeek} />}
          {activeTab === 'info'     && <InformationPersonnelleTab />}
        </div>
      </div>
    </div>
  );
};

export default MonEspaceTab;
